import { AnimatePresence, motion } from 'motion/react'

interface HealthBadgeProps {
    count: number | undefined
    /** Screen-reader text, e.g. "12 unreferenced items". The digit alone says nothing. */
    label: string
    tone?: 'danger' | 'amber'
}

const TONES = {
    danger: 'bg-danger/10 text-danger',
    amber: 'bg-amber-100 text-amber-900',
} as const

/**
 * The count on a Prune or Merge button.
 *
 * Renders nothing at 0 or while the count is still loading — a button reading
 * "Prune unreferenced (0)" is worse than the bare label, because it spends
 * attention to say there is nothing to do. The badge appearing IS the signal.
 */
export default function HealthBadge({ count, label, tone = 'danger' }: HealthBadgeProps) {
    const show = typeof count === 'number' && count > 0
    return (
        <AnimatePresence>
            {show && (
                <motion.span
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.6 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    style={{ transformOrigin: 'left center' }}
                    className={`ml-0.5 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs font-semibold tabular-nums ${TONES[tone]}`}
                >
                    <span aria-hidden="true">{count}</span>
                    <span className="sr-only">{label}</span>
                </motion.span>
            )}
        </AnimatePresence>
    )
}
